import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import Layout from "../../components/Layout";
import api from "../../api/axios";
import { toast } from "react-toastify";
import "./CustomerDetail.scss";

const PERIOD_TR = {
  BELIRTILMEDI: "Belirtilmedi",
  HAFTALIK: "1 Haftalık",
  IKIHAFTALIK: "2 Haftalık",
  UCHAFTALIK: "3 Haftalık",
  AYLIK: "1 Aylık",
  IKIAYLIK: "2 Aylık",
  UCAYLIK: "3 Aylık",
  ALTYAYLIK: "6 Aylık",
  YILLIK: "1 Yıllık",
};

const ONLINE_MS = 2 * 60 * 1000;
const IDLE_MS = 10 * 60 * 1000;

/* --- Erişim listesi (müşteri) --- */
function CustomerAccessList({ customerId }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/access/customer/${customerId}`);
        const list = Array.isArray(data?.grants) ? data.grants : (Array.isArray(data) ? data : []);
        setRows(list);
      } catch {
        /* sessiz düş */
      }
    })();
  }, [customerId]);

  const { user } = useAuth();
  const isLevel2 = user?.role === "employee" && user?.level === 2;

  if (!rows.length) return <div className="empty">Erişim sahibi yok.</div>;

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Kişi</th>
          <th>Rol</th>
          <th>Kapsam</th>
          <th>Detay</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((g) => (
          <tr key={g.id}>
            <td className="strong">
              {(g.owner?.firstName || "") + " " + (g.owner?.lastName || "")}{" "}
              {!isLevel2 && <span className="muted">({g.owner?.email})</span>}
            </td>
            <td>{g.owner?.role || "—"}</td>
            <td>{g.scopeType === "CUSTOMER" ? "Müşteri-Genel" : "Mağaza"}</td>
            <td>
              {g.scopeType === "CUSTOMER"
                ? (g.customer?.title || g.customerId)
                : (g.store?.name || g.storeId)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLevel1 = user?.role === "employee" && user?.level === 1;
  const isLevel2 = user?.role === "employee" && user?.level === 2;

  const [customer, setCustomer] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const filteredStores = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t) return stores;
    return stores.filter((s) =>
      [s.name, s.code, s.city, s.phone, s.manager]
        .some((v) => (v ?? "").toString().toLowerCase().includes(t))
    );
  }, [query, stores]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/customers/${id}`);
      setCustomer(data);
      setStores(Array.isArray(data?.stores) ? data.stores : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Müşteri bilgisi alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // (Presence and relTime removed as they are no longer used for customers)

  const fmtDate = (val) => {
    if (!val) return "—";
    try { return new Date(val).toLocaleString("tr-TR"); }
    catch { return String(val); }
  };

  const fmtPeriod = (p) => PERIOD_TR[p] || "Belirtilmedi";

  const goEditCustomer = () => navigate(`/admin/customers/${id}/edit`);

  const deleteCustomer = async () => {
    if (!window.confirm("Bu müşteriyi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Müşteri silindi");
      navigate("/admin/customers");
    } catch (err) {
      toast.error(err.response?.data?.message || "Silinemedi");
    }
  };

  // ---- Operasyon metrikleri (STORE'lardan) ----
  const storeCount = stores.length;
  const totalArea = useMemo(
    () => stores.reduce((acc, s) => acc + (Number(s.areaM2) || 0), 0),
    [stores]
  );
  const placeTypes = useMemo(
    () => Array.from(new Set(stores.map(s => s.placeType).filter(Boolean))),
    [stores]
  );
  const pestTypes = useMemo(
   () => Array.from(new Set(stores.map(s => s.pestType).filter(Boolean))),
    [stores]
  );

  return (
    <Layout>
      <div className="customer-detail-page">
        <div className="header">
          {/* Sol blok: başlık + presence (Avatar BLOĞU KALDIRILDI) */}
          <div className="identity">
            <div className="title-wrap">
              <h1 className="title">{customer?.title || "—"}</h1>
            </div>
          </div>

          {/* Sağ blok: aksiyonlar */}
          <div className="actions">
            {!isLevel2 && (
              <>
                <button className="btn btn-edit" onClick={goEditCustomer}>Düzenle</button>
                <button className="btn btn-danger" onClick={deleteCustomer}>Sil</button>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div className="card">Yükleniyor...</div>
        ) : !customer ? (
          <div className="card">Kayıt bulunamadı.</div>
        ) : (
          <>
            {/* Üst bilgi blokları */}
            <div className="top-grid">
              <section className="card">
                <div className="card-title">Genel Bilgiler</div>
                <div className="kv">
                  <div><b>Müşteri Kodu</b><span>{customer.code || "—"}</span></div>
                  <div><b>Şehir</b><span>{customer.city || "—"}</span></div>
                  {!isLevel2 && (
                    <div><b>Email</b><span className="email">{customer.email || "—"}</span></div>
                  )}
                  <div><b>Sorumlu Personel</b><span>{customer.employee?.fullName || "—"}</span></div>
                  <div className="full"><b>Merkez Adres</b><span>{customer.address || "—"}</span></div>
                </div>
              </section>

              <section className="card">
                <div className="card-title">İletişim</div>
                <div className="kv">
                  <div><b>Yetkili</b><span>{customer.contactFullName || "—"}</span></div>
                  <div><b>Telefon</b><span>{customer.phone || "—"}</span></div>
                  <div><b>GSM</b><span>{customer.gsm || "—"}</span></div>
                  {!isLevel2 && (
                    <>
                      <div><b>Vergi Dairesi</b><span>{customer.taxOffice || " —"}</span></div>
                      <div><b>Vergi No / TCKN</b><span>{customer.taxNumber || "—"}</span></div>
                    </>
                  )}
                </div>
              </section>

              {/* Operasyon bilgileri STORE'lardan türetiliyor */}
              <section className="card">
                <div className="card-title">Operasyon</div>
                <div className="kv">
                  <div><b>Mağaza Sayısı</b><span>{storeCount}</span></div>
                  <div><b>Toplam Uygulama Alanı (m²)</b><span>{totalArea || "—"}</span></div>
                  <div><b>Uygulama Yeri Tür(leri)</b><span>{placeTypes.length ? placeTypes.join(", ") : "—"}</span></div>
                  <div><b>Hedef Zararlı Tür(leri)</b><span>{pestTypes.length ? pestTypes.join(", ") : "—"}</span></div>
                </div>
              </section>

              <section className="card">
                <div className="card-title">Sistem</div>
                <div className="kv">
                  <div><b>Oluşturulma</b><span>{fmtDate(customer.createdAt)}</span></div>
                  <div><b>Güncelleme</b><span>{fmtDate(customer.updatedAt)}</span></div>
                </div>
              </section>
            </div>

            {/* Mağazalar + Erişim Sahipleri */}
            <div className="bottom-grid">
              <section className="card stores">
                <div className="card-title">Mağazalar</div>

                <div className="store-toolbar">
                  <div className="search">
                    <input
                      type="search"
                      placeholder="Mağaza ara (ad, kod, şehir, yetkili)…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                    {query && (
                      <button
                        type="button"
                        className="clear"
                        aria-label="Temizle"
                        onClick={() => setQuery("")}
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {!isLevel2 && (
                    <Link className="btn primary" to={`/admin/customers/${id}/stores/new`}>
                      + Mağaza Ekle
                    </Link>
                  )}
                </div>

                {stores.length === 0 ? (
                  <div className="empty">Kayıtlı mağaza yok.</div>
                ) : filteredStores.length === 0 ? (
                  <div className="empty">Aramanıza uygun mağaza bulunamadı.</div>
                ) : (
                  <div className="store-table-wrap">
                    <table className="store-table">
                      <thead>
                        <tr>
                          <th>Ad</th>
                          <th>Kod</th>
                          <th>Kısaltma</th>
                          <th>Şehir</th>
                          <th>Alan (m²)</th>
                          <th>Telefon</th>
                          <th>Yetkili</th>
                          <th>Durum</th>
                          <th>İşlem</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStores.map((s) => (
                          <tr
                            key={s.id}
                            className="clickable"
                            role="button"
                            tabIndex={0}
                            onClick={() => navigate(`/admin/stores/${s.id}`)}
                            onKeyDown={(e) => { if (e.key === "Enter") navigate(`/admin/stores/${s.id}`); }}
                          >
                            <td className="strong name-cell">
                              {s.name}
                              {!s.address?.trim() ? (
                                <span className="badge warn" title="Adres girilmemiş">Adres Yok</span>
                              ) : (
                                <span className="badge ok" title={s.address}>Adres Var</span>
                              )}
                            </td>
                            <td>{s.code || "—"}</td>
                            <td>{s.shortName || "—"}</td>
                            <td>{s.city || "—"}</td>
                            <td>{s.areaM2 || "—"}</td>
                            <td>{s.phone || "—"}</td>
                            <td>{s.manager || "—"}</td>
                            <td>
                              <span className={`badge ${s.isActive ? "ok" : "no"}`}>
                                {s.isActive ? "Aktif" : "Pasif"}
                              </span>
                            </td>
                            <td className="actions" onClick={(e) => e.stopPropagation()}>
                              {!isLevel2 && (
                                <>
                                  <Link className="btn" to={`/admin/stores/${s.id}/edit`}>Düzenle</Link>
                                  <button
                                    className="btn danger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!window.confirm("Bu mağazayı silmek istiyor musunuz?")) return;
                                      const prev = stores;
                                      setStores((x) => x.filter((xx) => xx.id !== s.id));
                                      api.delete(`/stores/${s.id}`)
                                        .then(() => toast.success("Mağaza silindi"))
                                        .catch((err) => {
                                          setStores(prev);
                                          toast.error(err.response?.data?.message || "Silinemedi");
                                        });
                                    }}
                                  >
                                    Sil
                                  </button>
                                </>
                              )}
                              {s.address?.trim() && (
                                <a
                                  className="btn ghost"
                                  href={`https://www.google.com/maps?q=${encodeURIComponent(s.address.trim())}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Haritada Aç"
                                >
                                  Haritada Aç
                                </a>
                              )}
                            </td>
                            <td className="go" onClick={(e) => e.stopPropagation()}>
                              <Link className="btn primary" to={`/admin/stores/${s.id}`}>
                                Mağaza
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {!isLevel2 && (
                <section className="card access">
                  <div className="card-title">
                    Erişim Sahipleri
                    {!isLevel2 && <Link className="btn" style={{ marginLeft: 8 }} to={`/admin/customers/${id}/access`}>Yönet</Link>}
                  </div>
                  <CustomerAccessList customerId={id} />
                </section>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
